import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchQuickLinks, updateQuickLinks, fetchResources } from '@gplibraries/react/api';
import { useAppContext, useTokenProvider, useDealerInfo, useConfig } from '@gplibraries/react/hooks';
/**
 * Hook for getting and setting a user's quick links.
 *
 * Uses the App Context to make quick links available to all components.
 *
 * @returns {[array,function]}
 */
export function useQuickLinks() {
    const [inProgress, setInProgress] = useState('');
    const [context, setContext] = useAppContext('quickLinks', []);
    const { dealerAccessApiScope, dealerAccessApiHost } = useConfig();
    const getToken = useTokenProvider({ scopes: [dealerAccessApiScope] });
    const { data: dealerInfo, loading: dealerInfoLoading } = useDealerInfo();
    const { isLoading: fetchLoading, error: fetchError, data } = useQuery({
        enabled: Boolean(getToken && dealerAccessApiHost),
        queryKey: ['quickLinks'],
        queryFn: () => getToken()
            .then(token => fetchQuickLinks(dealerAccessApiHost, token.accessToken, token?.accessTokenClaims?.oid ?? ''))
            .then(res => res.json())
    });
    const { data: resources } = useQuery({
        enabled: Boolean(getToken && dealerAccessApiHost),
        queryKey: ['resources', 'dealers'],
        queryFn: () => getToken()
            .then(token => fetchResources(dealerAccessApiHost, token.accessToken, 'dealers'))
            .then(res => res.json())
    });
    const { mutate: updateLinks } = useMutation({
        mutationFn: (newQuickLinks) => getToken()
            .then(token => updateQuickLinks(dealerAccessApiHost, token?.accessToken ?? '', token?.accessTokenClaims?.oid, data.meta.user_id, newQuickLinks))
            .then(res => res.json()),
        onError: (err) => {
            console.error(err);
            setInProgress('');
        },
        onSuccess: (newLinks) => {
            setContext(newLinks.data);
            setInProgress('');
        }
    });
    if (fetchError)
        console.error(fetchError);
    // filter quick links if necessary before displaying
    useEffect(() => {
        if (data?.data && !dealerInfoLoading && resources) {
            const filteredQuickLinks = data.data
                // First, filter out any null links. This happens when a user has a quick link that has been deleted.
                .filter((quickLink) => quickLink !== null)
                // Do access checking on specific dealers/territories
                .filter((quickLink) => {
                // If user is not acting as a dealer, don't need to check access.
                if (!dealerInfo)
                    return true;
                // don't need to check tm resources
                if (quickLink.type === 'node--tm_resource_link')
                    return true;
                //@todo: replace the resource type when it is written.
                const resource = resources?.data?.find((resource) => resource.id === quickLink.id);
                if (!resource)
                    return true;
                if (resource.field_hide_from_specific_dealers.includes(dealerInfo.dealer_number) ||
                    resource.field_hide_from_trty.includes(dealerInfo.territory.slice(0, 3)))
                    return false;
                if (resource.field_show_only_specific_dealers.length > 0 &&
                    !resource.field_show_only_specific_dealers.includes(dealerInfo.dealer_number))
                    return false;
                if (resource.field_show_to_trty.length > 0 &&
                    !resource.field_show_to_trty.includes(dealerInfo.territory.slice(0, 3)))
                    return false;
                return true;
            });
            setContext(filteredQuickLinks);
        }
    }, [data, resources, dealerInfoLoading]);
    async function addQuickLink(linkId, type) {
        setInProgress(linkId);
        if (inProgress || fetchLoading)
            return;
        if (!quickLinks.find((item) => item.id === linkId)) {
            const newQuickLinks = [
                ...quickLinks.map((link) => {
                    return {
                        type: link.type,
                        id: link.id,
                    };
                }),
                {
                    type: type,
                    id: linkId
                }
            ];
            updateLinks(newQuickLinks);
        }
    }
    async function removeQuickLink(linkId) {
        setInProgress(linkId);
        if (inProgress || loading)
            return;
        const existingLink = quickLinks.find((item) => item.id === linkId);
        if (existingLink) {
            // copy quick links so we can mutate
            const copy = [...quickLinks];
            copy.splice(copy.indexOf(existingLink), 1);
            const newQuickLinks = copy.map(link => {
                return {
                    type: link.type,
                    id: link.id,
                };
            });
            updateLinks(newQuickLinks);
        }
    }
    const quickLinks = context;
    const loading = fetchLoading;
    return { quickLinks, addQuickLink, removeQuickLink, loading, inProgress };
}
